import { type TrendChartYAxis, type TrendSeries } from '@payaca/ambrosia';
import { currencyCodeForRegion } from '@payaca/helpers/currencyHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import { useMemo } from 'react';
import { currencyLabel } from '../../../../helpers/currency';
import {
  ChartSeriesConfig,
  RateSeriesConfig,
  TimeSeriesPoint,
  Y_AXIS,
} from '../utils';

interface BuildStatsChartParams {
  timeSeries: TimeSeriesPoint[];
  chartSeries: ChartSeriesConfig[];
  rateSeries?: RateSeriesConfig;
  region?: string;
}

export interface TrendChartData {
  series: TrendSeries[];
  xAxisLabels: string[];
  yAxes: TrendChartYAxis[];
  tooltipFormatter: (
    val: number,
    opts: { seriesIndex: number; dataPointIndex: number }
  ) => string;
}

/**
 * Build column series data from monetary values (in major units for display).
 * Exported for testing.
 */
export const buildColumnSeriesData = (
  timeSeries: TimeSeriesPoint[],
  chartSeries: ChartSeriesConfig[]
): number[][] =>
  chartSeries.map((config) =>
    timeSeries.map((point) => {
      const valueObj = point[config.valueKey] as { value: number } | undefined;
      return (valueObj?.value ?? 0) / 100; // Convert to major units
    })
  );

/**
 * Calculate rate series data as percentages.
 * Exported for testing.
 */
export const buildRateSeriesData = (
  timeSeries: TimeSeriesPoint[],
  rateSeries: RateSeriesConfig
): number[] =>
  timeSeries.map((point) => {
    const numerator = point[rateSeries.numeratorKey] as number;
    const denominator = point[rateSeries.denominatorKey] as number;
    return denominator > 0 ? Math.round((numerator / denominator) * 100) : 0;
  });

/**
 * Calculate y-axis max with headroom.
 * Exported for testing.
 */
export const calculateYAxisMax = (values: number[]): number => {
  const maxValue = Math.max(...values, 1);
  return Math.ceil(maxValue * Y_AXIS.HEADROOM_MULTIPLIER);
};

/**
 * Build chart data in a format compatible with the TrendChart component.
 * Exported for testing.
 */
export const buildStatsChartOptions = ({
  timeSeries,
  chartSeries,
  rateSeries,
  region,
}: BuildStatsChartParams): TrendChartData | null => {
  if (!timeSeries?.length) {
    return null;
  }

  const columnSeriesData = buildColumnSeriesData(timeSeries, chartSeries);
  const yAxisMax = calculateYAxisMax(columnSeriesData.flat());
  const currencyCode = currencyCodeForRegion[region as AccountRegions];

  const series: TrendSeries[] = [
    ...chartSeries.map((config, i) => ({
      name: config.name,
      type: 'column' as const,
      data: columnSeriesData[i],
      color: config.color,
    })),
    ...(rateSeries
      ? [
          {
            name: rateSeries.name,
            type: 'line' as const,
            data: buildRateSeriesData(timeSeries, rateSeries),
            color: rateSeries.color,
          },
        ]
      : []),
  ];

  const xAxisLabels = timeSeries.map((point) => point.label);

  const yAxes: TrendChartYAxis[] = [
    {
      seriesNames: chartSeries.map((c) => c.name),
      formatter: (val: number) => currencyLabel(val * 100, currencyCode, false),
      tickAmount: 4,
      min: 0,
      max: yAxisMax,
    },
    ...(rateSeries
      ? [
          {
            seriesNames: [rateSeries.name],
            opposite: true as const,
            formatter: (val: number) => `${val}%`,
            tickAmount: 4,
            min: 0,
            max: 100,
          },
        ]
      : []),
  ];

  const tooltipFormatter = (
    val: number,
    opts: { seriesIndex: number; dataPointIndex: number }
  ) => {
    const dataPoint = timeSeries[opts.dataPointIndex];
    // Rate series is the last one
    if (rateSeries && opts.seriesIndex === chartSeries.length) {
      return `${val}%`;
    }
    // Value is already in major units, get count for secondary display
    const config = chartSeries[opts.seriesIndex];
    const count = dataPoint[config.countKey] as number;
    return `${currencyLabel(val * 100, currencyCode, false)} (${count})`;
  };

  return { series, xAxisLabels, yAxes, tooltipFormatter };
};

/**
 * Hook to build chart data for the TrendChart component.
 * Returns null if there's no time series data.
 */
export const useStatsChartOptions = ({
  timeSeries,
  chartSeries,
  rateSeries,
  region,
}: BuildStatsChartParams): TrendChartData | null => {
  return useMemo(
    () =>
      buildStatsChartOptions({
        timeSeries,
        chartSeries,
        rateSeries,
        region,
      }),
    [timeSeries, chartSeries, rateSeries, region]
  );
};
